import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AssessmentQuestion,
  normalizeOptions,
  resolveAccountSourceKey,
} from '../../support/assessments';
import SalesforceAccountLookupInput from './inputs/SalesforceAccountLookupInput';
import SalesforceOpportunityLookupInput from './inputs/SalesforceOpportunityLookupInput';
import DocumentUploadInput from './inputs/DocumentUploadInput';
import SalesforceAccountFileInput from './inputs/SalesforceAccountFileInput';
import DataImportInput from './inputs/DataImportInput';

/**
 * Renders the one input a question needs, chosen by its `response_type`.
 *
 * A controlled component: the parent owns the answer and passes it down, so validation, the
 * `visible_when` conditional logic and the save payload all read from one source. The value shapes
 * match what `emptyAnswer` seeds and what the response row stores — a string for text/number/date/
 * scale/file, a boolean for yes-no, a string for single choice, a string[] for multi-choice, and an
 * object for the upload / Salesforce-lookup types.
 *
 * `answersByKey` and `questions` are only needed by `sf_account_file`, which uploads to the account
 * chosen in a coupled `sf_account` question — it reads that question's answer to know the account.
 */

type Props = {
  question: AssessmentQuestion;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  /** Every answer so far, keyed by question key — used to resolve the coupled account. */
  answersByKey?: Record<string, any>;
  /** Every question in the assessment — used to find the coupled `sf_account` question. */
  questions?: AssessmentQuestion[];
  /** The response set being filled — passed to `data_import` so its dataset links to this set. */
  responseSetId?: number | null;
  /** Who is filling the assessment — recorded as `updated_by` on imported datasets/tiles. */
  respondent?: string | null;
};

/** Scale bounds live in `response_options`; fall back to a 1–5 scale when unset. */
const scaleConfig = (raw: any): { min: number; max: number; minLabel?: string; maxLabel?: string } => {
  const min = Number(raw?.min);
  const max = Number(raw?.max);
  return {
    min: Number.isFinite(min) ? min : 1,
    max: Number.isFinite(max) ? max : 5,
    minLabel: raw?.minLabel,
    maxLabel: raw?.maxLabel,
  };
};

const AssessmentQuestionInputBase: React.FC<Props> = ({
  question,
  value,
  onChange,
  disabled,
  answersByKey,
  questions,
  responseSetId,
  respondent,
}) => {
  switch (question.response_type) {
    case 'textarea':
      return (
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <TextField
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );

    case 'date':
      return (
        <TextField
          type="date"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'file':
      return (
        <TextField
          fullWidth
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Link or reference to the file"
          helperText="Paste a link or reference; file bytes are not stored here."
        />
      );

    case 'boolean':
      return (
        <ToggleButtonGroup
          exclusive
          value={value === true ? 'yes' : value === false ? 'no' : null}
          onChange={(_e, next) => {
            if (next === null) return;
            onChange(next === 'yes');
          }}
          disabled={disabled}
          size="small"
        >
          <ToggleButton value="yes">Yes</ToggleButton>
          <ToggleButton value="no">No</ToggleButton>
        </ToggleButtonGroup>
      );

    case 'scale': {
      const { min, max, minLabel, maxLabel } = scaleConfig(question.response_options);
      const steps: number[] = [];
      for (let i = min; i <= max; i += 1) steps.push(i);
      return (
        <Box>
          <ToggleButtonGroup
            exclusive
            value={value === '' || value === undefined || value === null ? null : String(value)}
            onChange={(_e, next) => {
              if (next === null) return;
              onChange(next);
            }}
            disabled={disabled}
            size="small"
          >
            {steps.map((step) => (
              <ToggleButton key={step} value={String(step)}>
                {step}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {(minLabel || maxLabel) && (
            <Box display="flex" justifyContent="space-between" mt={0.5} maxWidth={320}>
              <Typography variant="caption" color="text.secondary">
                {minLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {maxLabel}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    case 'single_choice': {
      const options = normalizeOptions(question.response_options);
      return (
        <FormControl disabled={disabled}>
          <RadioGroup value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }

    case 'multi_choice': {
      const options = normalizeOptions(question.response_options);
      const selected: string[] = Array.isArray(value) ? value.map(String) : [];
      const toggle = (optionValue: string) => {
        const next = selected.includes(optionValue)
          ? selected.filter((v) => v !== optionValue)
          : [...selected, optionValue];
        onChange(next);
      };
      return (
        <FormControl disabled={disabled}>
          <FormGroup>
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={selected.includes(option.value)}
                    onChange={() => toggle(option.value)}
                  />
                }
                label={option.label}
              />
            ))}
          </FormGroup>
        </FormControl>
      );
    }

    case 'sf_account':
      return (
        <SalesforceAccountLookupInput value={value ?? null} onChange={onChange} disabled={disabled} />
      );

    case 'sf_opportunity':
      return (
        <SalesforceOpportunityLookupInput
          value={value ?? null}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'document_upload':
      return <DocumentUploadInput value={value ?? null} onChange={onChange} disabled={disabled} />;

    case 'sf_account_file': {
      const sourceKey = resolveAccountSourceKey(question, questions || []);
      const sourceQuestion = (questions || []).find((q) => q.key === sourceKey);
      const accountId = sourceKey ? answersByKey?.[sourceKey]?.id ?? null : null;
      return (
        <SalesforceAccountFileInput
          value={value ?? null}
          onChange={onChange}
          accountId={accountId}
          sourceQuestionText={sourceQuestion?.text}
          disabled={disabled}
        />
      );
    }

    case 'data_import': {
      // If this import is coupled to an `sf_account` answer (same resolution the
      // `sf_account_file` type uses), the raw file is also attached to that account in Salesforce.
      const sourceKey = resolveAccountSourceKey(question, questions || []);
      const accountId = sourceKey ? answersByKey?.[sourceKey]?.id ?? null : null;
      return (
        <DataImportInput
          value={value ?? null}
          onChange={onChange}
          disabled={disabled}
          responseSetId={responseSetId ?? null}
          respondent={respondent ?? null}
          accountId={accountId}
        />
      );
    }

    case 'text':
    default:
      return (
        <TextField
          fullWidth
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
  }
};

/**
 * Memoized so that typing in one field does not re-render every other question's input — on a long
 * form (the full-page demo has ~37) that churn was the source of a visible flicker. This only holds
 * when the parent passes a *stable* `onChange` per question and does not pass the whole answers map
 * to inputs that do not need it; see `AssessmentFill`.
 */
const AssessmentQuestionInput = React.memo(AssessmentQuestionInputBase);

export default AssessmentQuestionInput;
